import React from 'react';
import { Input, Textarea } from '../../ui/Input';
import { allergenFamilyOf, allergensOfIngredient, ingredientsBlockingRestriction, keywordsForRestriction, RESTRICTION_GROUPS } from '../../../../shared/allergenReference.js';
import { ChipListInput, IngredientEditor, TracesEditor, type DishIngredient, type DishVariant } from './MenuCatalogEditors';

interface DishForm {
    name: string; category: string; description: string;
    ingredients: DishIngredient[]; allergens: string[]; dietary_tags: string[];
    incompatible_tags: string[]; variants: DishVariant[]; traces: string[];
}

/**
 * Le formulaire d'un plat, sorti de MenuCatalogPage.tsx qui dépassait la limite de 500 lignes.
 *
 * Les trois calculs qui l'accompagnent (allergènes déduits, ingrédients inconnus, restrictions bloquées)
 * viennent avec lui : ils ne servent qu'ici, les laisser dans la page aurait été les séparer de leur seul
 * usage. Les propriétés portent exactement le nom des variables que le formulaire utilisait auparavant —
 * pas une ligne du contenu n'a été retouchée, ce qui rend le déplacement vérifiable à l'œil nu.
 */
export function MenuDishForm({ dishForm, setDishForm, knownCategories, allergenNames, allergenTerms, linkAllergen }: {
    dishForm: DishForm;
    setDishForm: (f: DishForm) => void;
    knownCategories: string[];
    allergenNames: string[];
    allergenTerms: any[];
    /** Asynchrone : le rattachement recharge le référentiel d'allergènes après écriture. */
    linkAllergen: (ingredient: string, allergen: string) => Promise<void>;
}) {
    // Allergènes du plat déduits des ingrédients et de leurs composants, au lieu d'être ressaisis à la main :
    // le champ libre d'avant permettait d'écrire un allergène absent des ingrédients (donc jamais détecté,
    // voir docs/prompt_extraction_carte_gemini.md) ou d'en oublier un pourtant présent. Ici, ce qui s'affiche
    // est exactement ce qui déclenchera une alerte.
    const detectedAllergens = React.useMemo(() => {
        const set = new Set<string>();
        dishForm.ingredients.forEach((i) => {
            [i.name, ...(i.components || []).map((c) => c.name)].forEach((term) => {
                const fam = allergenFamilyOf(term);
                if (fam) set.add(fam);
            });
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [dishForm.ingredients]);

    const unrecognized = React.useMemo(
        () => dishForm.ingredients.filter((i) => allergensOfIngredient(i, allergenTerms).length === 0).map((i) => i.name),
        [dishForm.ingredients, allergenTerms]);

    const formBlocked = React.useMemo(() => RESTRICTION_GROUPS
        .map((g) => {
            const hits = ingredientsBlockingRestriction(g.name, dishForm.ingredients, keywordsForRestriction(g.name, allergenTerms), allergenTerms);
            if (!hits.length) return null;
            return {
                name: g.name,
                adaptable: hits.every((h) => h.ingredient.removable),
                because: hits.map((h) => h.ingredient.name + (h.via ? ` (${h.via.toLowerCase()})` : '')),
            };
        })
        .filter(Boolean) as { name: string; adaptable: boolean; because: string[] }[],
        [dishForm.ingredients, allergenTerms]);

    const dishFormFields = (
        <div className="space-y-3">
            <Input value={dishForm.name} onChange={(e) => setDishForm({ ...dishForm, name: e.target.value })} placeholder="Nom du plat (ex: Saumon fumé)" autoFocus />
            <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Catégorie</label>
                {knownCategories.length === 0 ? (
                    <p className="text-[11px] text-content-muted italic">Aucune catégorie pour l'instant — crée-en une depuis l'onglet "Formules" (ex: Entrée, Plat, Dessert), en ajoutant une catégorie à une formule.</p>
                ) : (
                    <select value={dishForm.category} onChange={(e) => setDishForm({ ...dishForm, category: e.target.value })}
                        className="w-full bg-surface border border-border rounded-md px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500">
                        <option value="">Aucune catégorie</option>
                        {knownCategories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                )}
            </div>
            <Textarea value={dishForm.description} onChange={(e) => setDishForm({ ...dishForm, description: e.target.value })} placeholder="Description (optionnel)" rows={2} />
            <div className="border-t border-border pt-3">
                <p className="text-[11px] font-black text-content uppercase tracking-widest">1 · Ce qu'il y a dans l'assiette</p>
                <p className="text-[10px] text-content-muted mb-2">
                    Tout le reste en découle : allergies détectées, restrictions, adaptations proposées au convive.
                    C'est la seule partie vraiment importante à remplir.
                </p>
                <IngredientEditor ingredients={dishForm.ingredients} onChange={(v) => setDishForm({ ...dishForm, ingredients: v })}
                    allergensOf={(i) => allergensOfIngredient(i, allergenTerms)} allergenNames={allergenNames} onLinkAllergen={linkAllergen} />
            </div>

            <div className="border-t border-border pt-3 space-y-1.5">
                <p className="text-[11px] font-black text-content uppercase tracking-widest">2 · Allergènes détectés</p>
                {/* Compte des ingrédients qui ne déclenchent rien : c'est le seul endroit où l'on peut
                    s'apercevoir qu'un plat est mal configuré AVANT qu'un convive s'y heurte. */}
                {unrecognized.length > 0 && (
                    <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-2 py-1.5 mb-1.5">
                        {unrecognized.length} ingrédient(s) sans allergène détecté : {unrecognized.join(', ')}.
                        Déplie-les ci-dessus pour les rattacher — s'ils sont inoffensifs (citron, salade…), il n'y a rien à faire.
                    </p>
                )}
                {detectedAllergens.length > 0 ? (
                    <>
                        <div className="flex flex-wrap gap-1">
                            {detectedAllergens.map((a) => (
                                <span key={a} className="text-[10px] font-semibold bg-warning-500/10 border border-warning-500/20 rounded-full px-2 py-0.5 text-warning-600 dark:text-warning-400">{a}</span>
                            ))}
                        </div>
                        <p className="text-[10px] text-content-muted">
                            Calculé à partir des ingrédients ci-dessus, rien à saisir.
                        </p>
                    </>
                ) : (
                    <p className="text-[10px] text-content-muted">
                        Aucun allergène reconnu dans les ingrédients. Si le plat en contient, détaille les préparations en composants
                        (« Sauce » → beurre, vin blanc) pour qu'ils soient détectés.
                    </p>
                )}
            </div>

            {/* Restrictions déduites, expliquées : « ✕ végétarien » sur un velouté de légumes n'a de sens que si
                l'on voit que c'est le bouillon de volaille qui la déclenche. C'est aussi ici qu'on comprend
                qu'il suffirait de cocher « possible de retirer » sur l'élément fautif pour rendre le plat
                adaptable. Question d'Emeni (2026-08-23). */}
            {formBlocked.length > 0 && (
                <div className="border-t border-border pt-3 space-y-1.5">
                    <p className="text-[11px] font-black text-content uppercase tracking-widest">Restrictions incompatibles</p>
                    <p className="text-[10px] text-content-muted">
                        Déduites des ingrédients, rien à saisir. « Adaptable » veut dire que le convive pourra faire retirer
                        ce qui gêne ; sinon il devra choisir un autre plat.
                    </p>
                    {formBlocked.map((b) => (
                        <div key={b.name} className={`text-[10px] rounded-lg px-2 py-1 ${b.adaptable ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'}`}>
                            <span className="font-bold">{b.adaptable ? '~' : '✕'} {b.name}</span>
                            <span className="block">
                                à cause de {b.because.join(', ')}
                                {!b.adaptable && ' — coche « possible de retirer » sur cet élément pour rendre le plat adaptable'}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <div className="border-t border-border pt-3">
                <p className="text-[11px] font-black text-content uppercase tracking-widest">3 · Contamination croisée</p>
                <TracesEditor traces={dishForm.traces} onChange={(v) => setDishForm({ ...dishForm, traces: v })} />
            </div>

            {/* "Restrictions incompatibles" et "Variantes" ne sont plus saisis : une incompatibilité dépend de
                ce que le convive peut faire retirer, et une alternative se déduit des ingrédients retirables.
                Les écrire en dur contredisait les adaptations réellement disponibles. Retour d'Emeni
                (2026-08-23) : "les restrictions incompatibles sur un plat dépendent également de ce qu'il y a
                comme alternative", "il faut que ce soit intelligent sans que j'aie à trop sélectionner". */}
            <details className="border-t border-border pt-3">
                <summary className="text-[11px] font-black text-content-muted uppercase tracking-widest cursor-pointer">
                    Exception (rarement utile)
                </summary>
                <div className="pt-2">
                    <ChipListInput label="Ce plat respecte quand même…" placeholder="Ex: halal"
                        values={dishForm.dietary_tags} onChange={(v) => setDishForm({ ...dishForm, dietary_tags: v })} />
                    <p className="text-[10px] text-content-muted mt-1">
                        À n'utiliser que pour lever une alerte injustifiée — par exemple « halal » si ta viande est certifiée,
                        alors que le système la signalerait par prudence. Sans effet sur les allergènes : un plat déclaré
                        « sans lactose » à tort continuera de signaler son beurre.
                    </p>
                </div>
            </details>
        </div>
    );
    return dishFormFields;
}
